import React, { useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Box,
  Grid2,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import dayjs from "dayjs";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";
import { addDocumentNote, deleteDocumentNote, getDocumentNotes, updateDocumentNote } from "../API/DocumentNotesApi";
import { convertUTCToTimeZoneDateTimeString } from "../Utility/DateTimeUtility";

// Dummy user for demonstration
const currentUser = { name: "Dr. John Doe" };

const FollowUpNotes = ({documentId, documentType}) => {
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    const loadNotes = async () => {
        const result = await getDocumentNotes(auth, documentId, documentType);
        if (result.isSuccess) {
          const sortedNotes = result.data.sort((a, b) => new Date(b.lastModifiedOn) - new Date(a.lastModifiedOn));
          setNotes(sortedNotes);
        } else {
          showSnackbar("Failed to load notes: " + result.data, "error");
        }
    };
    loadNotes();
  }, [documentId, documentType, auth]);
      // Simulate fetching notes from an API

  const refreshNotes = async () => {
    const result = await getDocumentNotes(auth, documentId, documentType);
    if (result.isSuccess) {
      const sortedNotes = result.data.sort((a, b) => new Date(b.lastModifiedOn) - new Date(a.lastModifiedOn));
      setNotes(sortedNotes);
    } else {
      showSnackbar("Failed to refresh notes: " + result.data, "error");
    }
  };

  // Add new note
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    const newNote = {
        content: noteText,
        documentId: documentId,
        documentType: documentType,
      };

    const result = await addDocumentNote(auth, newNote);
    console.log("Add Note Result:", JSON.stringify(result));
    if (result?.isSuccess) {
      refreshNotes();
      setNoteText("");
      showSnackbar("Note added successfully", "success");
    } else {
      showSnackbar("Failed to add note: " + result.data, "error");
    }
  };

  // Delete note
  const handleDelete = async (id) => {
    const result = await deleteDocumentNote(auth, id);
    if (result?.isSuccess) {
      setNotes(notes.filter((n) => n.id !== id));
      showSnackbar("Note deleted successfully", "success");
    } else {
      showSnackbar("Failed to delete note: " + result.data, "error");
    }
    refreshNotes();
  };

  // Start editing
  const handleEdit = (id, text) => {
    setEditId(id);
    setEditText(text);
  };

  // Save edit
  const handleSaveEdit = async (id) => {
    const result = await updateDocumentNote(auth, { id, content: editText });
    if (result?.isSuccess) {
      refreshNotes();
      setEditId(null);
    } else {
      showSnackbar("Failed to update note: " + result.data, "error");
    }
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setEditText("");
  };

  return (
    <Grid2 container size={12} sx={{ maxHeight: "80vh", overflow: "auto" }} >
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        p: { xs: 2, md: 2 },
        background: " #f5f7fa",
        borderRadius: 3,
        boxShadow: 1,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 0 }}>
        {/* Add Note */}
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Add Follow-up Note
        </Typography>
        <TextField
          label="Enter follow-up note"
          multiline
          minRows={3}
          maxRows={6}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          fullWidth
          variant="outlined"
          sx={{ mb: 2, background: "#fff", borderRadius: 2 }}
        />
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNote}
            disabled={!noteText.trim()}
            sx={{ textTransform: "none" }}
          >
            Add Note
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* List of Notes */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          Follow-up Notes
        </Typography>
        <Box display="flex" flexDirection="column" gap={2} sx={{ overflowY: "auto", flex: 1 }}>
          {notes.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No follow-up notes yet.
            </Typography>
          )}
          {notes.map((note) => (
            <Card key={note.id} variant="outlined" sx={{ background: "#f9f9f9", borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {convertUTCToTimeZoneDateTimeString(note.createdOn, auth?.selectedFacility?.timeZone)}{" "}
                  </Typography>
                  <Box>
                    {editId === note.id ? (
                      <>
                        <Tooltip title="Save">
                          <IconButton
                            color="primary"
                            onClick={() => handleSaveEdit(note.id)}
                            size="small"
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            color="secondary"
                            onClick={handleCancelEdit}
                            size="small"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(note.id, note.content)}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(note.id)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                {editId === note.id ? (
                  <TextField
                    multiline
                    minRows={2}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 1, background: "#fff", borderRadius: 1 }}
                  />
                ) : (
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {note.content}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  By: {note.createdByUserName}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </CardContent>
    </Card>
    </Grid2>
  );
};

export default FollowUpNotes;